/**
 * Alerts Page - AI-NATIVE DESIGN SYSTEM
 * 
 * Lists all USER-generated alert configurations for the merchant.
 * Displays alerts in a stacked list layout.
 * Allows creating new alerts via the alert-creation agent.
 */

import AppLayout from '@layouts/AppLayout';
import PageHeader from '@components/common/PageHeader';
import { Button, Loader, Stack, Text } from '@mantine/core';
import { Plus, Bell } from '@phosphor-icons/react';
import { useEffect, useState, type FC } from 'react';
import { useNavigate } from 'react-router';

import { type AlertWithCounts, fetchAlertsWithCounts } from './api';
import AlertRuleStackCard from './components/AlertRuleStackCard';

const Alerts: FC = () => {
    const navigate = useNavigate();
    const [alerts, setAlerts] = useState<AlertWithCounts[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        loadAlerts();
    }, []);

    const loadAlerts = async () => {
        setLoading(true);
        try {
            const data = await fetchAlertsWithCounts();
            setAlerts(data);
        } catch (error) {
            console.error('Failed to load alerts:', error);
        } finally {
            setLoading(false);
        }
    };

    // Navigate directly to AlertWorkbook - naming dialog shown there
    const handleCreateAlert = () => {
        navigate('/alerts/new');
    };

    return (
        <AppLayout>
            <div 
                className="flex h-full w-full flex-col overflow-y-auto page-shell"
                style={{ backgroundColor: '#FAFAFA' }}
            >
                {/* Shared PageHeader Component */}
                <PageHeader
                    icon="🔔"
                    title="Alerts"
                    subtitle="Manage your alert configurations"
                    actions={
                        <Button
                            leftSection={<Plus size={16} />}
                            onClick={handleCreateAlert}
                            variant="action"
                            radius="md"
                            size="sm"
                        >
                            Create New Alert
                        </Button>
                    }
                />

                {/* Content */}
                {loading ? (
                    <div className="flex flex-1 items-center justify-center">
                        <Stack align="center" gap="md">
                            <Loader size="md" color="gray" />
                            <Text size="sm" c="dimmed">
                                Loading alerts...
                            </Text>
                        </Stack>
                    </div>
                ) : alerts.length === 0 ? (
                    <div className="flex flex-1 items-center justify-center">
                        <Stack align="center" gap="md" className="max-w-sm text-center">
                            <div 
                                className="flex items-center justify-center rounded-full"
                                style={{ 
                                    width: 64, 
                                    height: 64, 
                                    backgroundColor: 'rgba(0,0,0,0.04)' 
                                }}
                            >
                                <Bell size={32} className="text-slate-400" />
                            </div>
                            <Text fw={500} size="md" className="text-slate-700">
                                No alerts configured yet
                            </Text>
                            <Text size="sm" c="dimmed">
                                Create your first alert to start monitoring your inventory and get notified when important events occur.
                            </Text>
                            <Button
                                leftSection={<Plus size={16} />}
                                onClick={handleCreateAlert}
                                variant="outline"
                                color="dark"
                                radius="md"
                                size="sm"
                                mt="sm"
                            >
                                Create Your First Alert
                            </Button>
                        </Stack>
                    </div>
                ) : (
                    <Stack gap="md">
                        {/* Section Header - matching Mission Control styling */}
                        <Text 
                            className="text-slate-900"
                            style={{ 
                                fontSize: 18, 
                                fontWeight: 600,
                                lineHeight: 1.3 
                            }}
                        >
                            Active alerts ({alerts.length})
                        </Text>
                        
                        {/* Alert Stack Cards */}
                        {alerts.map((alert) => (
                            <AlertRuleStackCard
                                key={alert.alertId}
                                alert={alert}
                                onDelete={(deletedId) =>
                                    setAlerts((prev) => prev.filter((a) => a.alertId !== deletedId))
                                }
                            />
                        ))}
                    </Stack>
                )}
            </div>
        </AppLayout>
    );
};

export default Alerts;
